/**
 * @module AlgoliaRobodrills
 * @description Initializes and manages Algolia product search grid for Robodrills.
 *
 * Uses the base product search grid component factory for consistency across product types.
 * This is specifically for product search grids, not general site search.
 */

import { createProductSearchGrid } from './BaseProductSearchGrid.js';
import { createProductConfig } from '@js/algolia/createProductConfig.js';
import { robodrillsConfig } from '@js/config/algolia.js';

/**
 * Get array of specs to display for a Robodrill
 * @param {Object} result - The search result
 * @param {Object} translations - Translation labels for spec names
 * @returns {Array} Array of spec objects with icon, label, value, and unit
 */
function getSpecs(result, translations = {}) {
  const specs = [];
  const tableSizeLabel = translations.tableSize || 'Table Size';
  const xTravelLabel = translations.xTravel || 'X Travel';
  const maxLoadLabel = translations.maxLoad || 'Max Load';

  if (result.tableSizeX && result.tableSizeY) {
    specs.push({
      icon: 'table-size',
      label: tableSizeLabel,
      value: `${result.tableSizeX} × ${result.tableSizeY}`,
      unit: 'mm',
    });
  }

  if (result.xAxisTravel) {
    specs.push({
      icon: 'x-travel',
      label: xTravelLabel,
      value: result.xAxisTravel,
      unit: 'mm',
    });
  }

  if (result.maxTableLoad) {
    specs.push({
      icon: 'max-load',
      label: maxLoadLabel,
      value: result.maxTableLoad,
      unit: 'kg',
    });
  }

  return specs;
}

/**
 * Check if Robodrill has any specs to display
 * @param {Object} result - The search result
 * @returns {boolean}
 */
function hasSpecs(result) {
  return !!(result.tableSizeX || result.xAxisTravel || result.maxTableLoad);
}

/**
 * Format Algolia result to match product-card component structure
 *
 * This function remaps Algolia search result fields to match the structure expected by
 * the product-card component (_components/modules/product-card.twig).
 *
 * @param {Object} result - The raw Algolia search result object
 * @param {Object} translations - Translation labels for card attributes
 * @returns {Object} Formatted data matching product-card structure
 */
function formatCardData(result, translations) {
  return {
    heading: result.title || '',
    url: result.absoluteUri || result.url || '#',
    image: result.productImage || null,
    category:
      result.productSeries && result.productSeries.length
        ? result.productSeries[0]
        : null,
    description: result.description || result.tagline || null,
    attributes: getSpecs(result, translations).map((spec) => ({
      label: spec.label,
      value: spec.value + (spec.unit ? ` ${spec.unit}` : ''),
      icon: spec.icon,
    })),
    legacy: result.legacy || false,
  };
}

// Create and export the Alpine component using the base factory
export default createProductSearchGrid({
  appId: robodrillsConfig.app_id,
  apiKey: robodrillsConfig.api_key,
  indexName: robodrillsConfig.index,
  configureWidget: createProductConfig('[data-search-robodrills]'),
  formatCardData,
  getSpecs,
  hasSpecs,
  scrollToSelector: '[data-search-robodrills]',
});
